import type { ManagedDocumentContent } from '@cdf/api-client-react';
import { ManagedDocumentEditor } from '@/components/files/managed-document-editor';

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function document(value: unknown): value is ManagedDocumentContent {
  let remaining = 10000;
  function node(value: unknown, depth: number): boolean {
    if (--remaining < 0 || depth > 20 || !record(value) || typeof value.type !== 'string') return false;
    if (value.text !== undefined && typeof value.text !== 'string') return false;
    if (value.attrs !== undefined && !record(value.attrs)) return false;
    if (value.marks !== undefined && (!Array.isArray(value.marks) || value.marks.length > 20 || !value.marks.every(mark => record(mark) && typeof mark.type === 'string' && (mark.attrs === undefined || record(mark.attrs))))) return false;
    return value.content === undefined || Array.isArray(value.content) && value.content.every(child => node(child, depth + 1));
  }
  return record(value) && value.type === 'doc' && Array.isArray(value.content) && value.content.every(child => node(child, 0));
}
/** Human presentation of the server's exact, hash-bound review; no values are recalculated here. */
export function SowExactReview({ serialized }: { serialized: string }) {
  let value: unknown;
  try { value = JSON.parse(serialized); } catch { return <pre className="whitespace-pre-wrap break-all">{serialized}</pre>; }
  if (!record(value) || !document(value.content))
    return <pre className="whitespace-pre-wrap break-all">{serialized}</pre>;
  const validation = record(value.validation) ? value.validation : {};
  return <div className="min-w-0 space-y-3">
    <p className="font-medium">{String(value.title ?? 'Private SOW draft')}</p>
    <p>Template: {String(value.templateVersionId)} · {value.documentId ? 'Update existing draft revision ' + String(value.documentRevision) : 'Create one private Files draft'}</p>
    <p>{validation.readyForReview === true ? 'Ready for author review' : 'Incomplete draft — unresolved fields remain'}</p>
    <dl className="grid gap-2 sm:grid-cols-2">{['subtotal', 'gst', 'gross', 'materials', 'invoices', 'effortDays'].map(key =>
      <div key={key}><dt className="font-medium">{key}</dt><dd>{validation[key] == null ? 'Not confirmed' : String(validation[key]) + (key === 'effortDays' ? ' days' : ' AUD')}</dd></div>)}</dl>
    <ManagedDocumentEditor content={value.content} disabled onChange={() => {}} />
    <details className="rounded-md border p-2"><summary className="cursor-pointer">Exact facts, evidence and revision pins</summary>
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all">{serialized}</pre>
    </details>
  </div>;
}
